import type { GeoPoint } from 'tripPlanner/types/common'
type CartesianPoint = { x: number, y: number }

// Find the point, dist units away from the midpoint of a line that
// runs from 0,0 to width,height
// https://github.com/lux-group/www-le-customer/pull/10323#issuecomment-1318318762
export const xyPerpendicularToMidpoint = (
  width: number,
  height: number,
  flightDirection: FlightDirection,
  dist: number,
): CartesianPoint => {
  let perpendicularGradient = -width / height
  if (height === 0) perpendicularGradient = -9999 // avoid infinity

  const angle = Math.atan(perpendicularGradient)
  const deltaX = Math.cos(angle) * dist
  const deltaY = Math.sin(angle) * dist

  const midX = width / 2
  const midY = height / 2

  const y = midY + deltaY
  let x
  if (flightDirection === 'NE' || flightDirection === 'SW') {
    // the line slopes up-right in the bounding box.
    // The perpendicular point we want is north-WEST of the midpoint
    x = midX - deltaX
  } else {
    // the line slopes down-right in the bounding box.
    // The perpendicular point we want is north-EAST of the midpoint
    x = midX + deltaX
  }
  return {
    x: parseFloat(x.toFixed(1)),
    y: parseFloat(y.toFixed(1)),
  }
}

/*
 * A flight from eg. Fiji to Hawaii will cross the date line.
 * By default, Google Maps draws the line going ALL the way around
 * the other side of the earth.
 * The earth has 360 deg of longitude, from -180 to +180.
 * To force Maps to cross the date line, you must specify a bounding box with
 * longitudes outside that range.
 * Eg. a box from 0,+179 to 0,-179 will wrap round the earth.
 * But a box from 0,+179 to 0,+181 will be a tiny box joining the same points, across the date line
 * We assume any flight spanning over 180 deg longitude should cross the date line.
 */
export const shortestPathPoints = (
  start: GeoPoint,
  end: GeoPoint,
): Array<GeoPoint> => {
  const span = Math.abs(start.lng - end.lng)
  if (span <= 180) {
    return [start, end]
  }

  if (start.lng < end.lng) {
    return [{ ...start, lng: start.lng + 360 }, end]
  }

  return [start, { ...end, lng: end.lng + 360 }]
}

export type FlightDirection = 'NE' | 'SE' | 'NW' | 'SW'
export const flightDirection = (
  start: GeoPoint,
  end: GeoPoint,
): FlightDirection => {
  const northOrSouth = start.lat < end.lat ? 'N' : 'S'
  const eastOrWest = start.lng < end.lng ? 'E' : 'W'
  return `${northOrSouth}${eastOrWest}`
}

export const quadBezierAtT = (
  p0: CartesianPoint,
  p1: CartesianPoint,
  p2: CartesianPoint,
  t: number,
): CartesianPoint => {
  // https://stackoverflow.com/questions/5634460/quadratic-b%C3%A9zier-curve-calculate-points
  const x =
    Math.pow(1 - t, 2) * p0.x + 2 * (1 - t) * t * p1.x + Math.pow(t, 2) * p2.x
  const y =
    Math.pow(1 - t, 2) * p0.y + 2 * (1 - t) * t * p1.y + Math.pow(t, 2) * p2.y
  return { x, y }
}

export const quadBezierAngle = (
  p0: CartesianPoint,
  p1: CartesianPoint,
  p2: CartesianPoint,
  t: number,
): number => {
  // https://math.stackexchange.com/questions/885292/how-to-take-derivative-of-bezier-function
  const x = 2 * (1 - t) * (p1.x - p0.x) + 2 * t * (p2.x - p1.x)
  const y = 2 * (1 - t) * (p1.y - p0.y) + 2 * t * (p2.y - p1.y)

  // x, y represents a vector tangent to the curve
  // The gradient is the "rise over run" of that vector - ie, y/x
  // as per https://math.stackexchange.com/questions/1383102/calculate-gradient-partial-derivatives-of-bezier-curve
  let radians = Math.atan(y / x)

  // Now we need to figure if we need to rotate by the acute or the complementing obtuse angle
  if (x < 0 && y < 0) {
    // this is a NW flight. We want the complementary angle.
    // Rotate by 180 degrees counter-clockwise
    radians = radians - Math.PI
  } else if (x < 0 && y > 0) {
    // this is a SW flight. We want the complementary angle.
    // Rotate by 180 degrees clockwise
    radians = radians + Math.PI
  }
  return radians
}

export const directionalStartEnd = (
  width: number,
  height: number,
  direction: FlightDirection,
): [[number, number], [number, number]] => {
  switch (direction) {
    case 'SE':
      // start at the top left of the SVG, draw down to bottom right
      return [
        [0, 0],
        [width, height],
      ]
    case 'NW':
      // start at the bottom right of the SVG, draw up to top left
      return [
        [width, height],
        [0, 0],
      ]
    case 'NE':
      // start at the bottom left of the SVG, draw up to top right
      return [
        [0, height],
        [width, 0],
      ]
    case 'SW':
      // start at the top right of the SVG, draw down to bottom left
      return [
        [width, 0],
        [0, height],
      ]
    default:
      throw new Error('Unexpected value for direction')
  }
}
